import Vue from 'vue'
import Vuex from 'vuex'

import { vuexAiPlugin } from '@/plugins/aiPlugin'

import actions from './actions'
import getters from './getters'
import mutations from './mutations'
import { persistentUsersPlugin } from './persistentUsersPlugin'

Vue.use(Vuex)

export default new Vuex.Store({
  state: {
    isAdmin: false,
    isAreaManager: false,
    isInsightsViewer: false,
    fetching: 0,
    isAppInitialised: false,
    userPermissionsToContract: {},
    messages: [],
    contracts: [],
    employers: [],
    projects: [],
    contractTypes: [],
    packages: [],
    contractors: [],
    stepNames: [],
    notificationsDays: [],
    workflowsTypes: [],
    currencies: [],
    variationsStages: [],
    statuses: [],
    categories: [],
    workstreams: [],
    areas: [],
    localUsers: [],
    risks: [],
    guaranteesNames: []
  },

  getters: {
    [getters.isAppInitialised] (state) {
      return state.isAppInitialised
    },
    [getters.isFetching] (state) {
      return !!state.fetching
    },
    [getters.currenciesCodes] (state) {
      return state.currencies.map(currency => currency.code)
    }
  },

  mutations: {
    [mutations.SET_APP_INIT] (state) {
      state.isAppInitialised = true
    },
    [mutations.SET_CAN_USER_PERMISSIONS] (state, data) {
      state.userPermissionsToContract = data
    },
    [mutations.SET_EMPLOYERS] (state, data) {
      state.employers = data
    },
    [mutations.SET_CONTRACTORS] (state, data) {
      state.contractors = data
    },
    [mutations.SET_ADMIN_ROLE] (state, data) {
      state.isAdmin = data
    },
    [mutations.SET_AREA_MANAGER_ROLE] (state, data) {
      state.isAreaManager = data
    },
    [mutations.SET_INSIGHTS_ROLE] (state, data) {
      state.isInsightsViewer = data
    },
    [mutations.SET_CONTRACT_PROJECTS] (state, data) {
      state.projects = data
    },
    [mutations.SET_CONTRACT_TYPES] (state, data) {
      state.contractTypes = data
    },
    [mutations.SET_CONTRACT_PACKAGES] (state, data) {
      state.packages = data
    },
    [mutations.SET_STEPS_NAMES] (state, data) {
      state.stepNames = data
    },
    [mutations.SET_NOTIFICATION_DAYS] (state, data) {
      state.notificationsDays = data
    },
    [mutations.SET_WORKFLOWS_TYPES] (state, data) {
      state.workflowsTypes = data
    },
    [mutations.SET_CURRENCIES] (state, data) {
      state.currencies = data
    },
    [mutations.SET_WORKSTREAMS] (state, data) {
      state.workstreams = data
    },
    [mutations.SET_VARIATIONS_STAGES] (state, data) {
      state.variationsStages = data
    },
    [mutations.SET_STATUSES] (state, data) {
      state.statuses = data
    },
    [mutations.SET_CATEGORIES] (state, data) {
      state.categories = data
    },
    [mutations.SET_AREAS] (state, data) {
      state.areas = data
    },
    [mutations.SET_RISKS] (state, data) {
      state.risks = data
    },
    [mutations.ADD_STEP_NAME] (state, data) {
      state.stepNames.push(data)
    },
    [mutations.ADD_EMPLOYER] (state, data) {
      state.employers = [...state.employers, data]
    },
    [mutations.ADD_CONTRACTOR] (state, data) {
      state.contractors = [...state.contractors, data]
    },
    [mutations.CREATE_MESSAGE] (state, message) {
      state.messages.push({ isRead: false, ...message })
    },
    [mutations.SET_CONTRACTS] (state, data) {
      state.contracts = data
    },
    [mutations.IS_FETCHING] (state) {
      state.fetching++
    },
    [mutations.FETCHING_DONE] (state) {
      state.fetching--
    },
    [mutations.UPDATE_CONTRACT] (state, data) {
      Object.assign(
        state.contracts.find(i => i.id === data.id),
        data
      )
    },
    [mutations.SET_LOCAL_USERS] (state, data) {
      state.localUsers = { ...state.localUsers, ...data }
    },
    [mutations.GUARANTEES_NAMES] (state, data) {
      state.guaranteesNames = data
    }
  },

  actions: {
    [actions.initialiseApp] ({ commit, dispatch, rootState }) {
      commit(mutations.SET_APP_INIT)
    },
    async [actions.callApi] ({ commit, dispatch, rootState }, payload) {
      commit(mutations.IS_FETCHING)
      try {
        const response = await this._vm.$api(payload.url, {
          method: payload.method || 'get',
          json: payload.body ? payload.body : undefined,
          searchParams: new URLSearchParams(payload.params),
          headers: payload.headers,
          responseType: payload.responseType
        }).json()
        return response
      } catch (error) {
        const errorResponse = error.response ? await error.response.json() : error
        error.parsedResponse = errorResponse

        const isApplicationExists = errorResponse.statusCode === 400 && errorResponse.message === 'APPLICATION_EXISTS'
        !isApplicationExists && dispatch('createMessage', { text: errorResponse.message })

        throw error
      } finally {
        commit(mutations.FETCHING_DONE)
      }
    },
    async [actions.callGraph] ({ commit, dispatch }, payload) {
      commit(mutations.IS_FETCHING)
      try {
        const response = await this._vm.$graph(payload.url, {
          method: payload.method || 'get',
          searchParams: new URLSearchParams(payload.params),
          headers: payload.headers
        }).json()
        return response.value
      } catch (error) {
        const errorResponse = error.response ? await error.response.json() : error
        error.parsedResponse = errorResponse
        dispatch('createMessage', { text: errorResponse.message })
        throw error
      } finally {
        commit(mutations.FETCHING_DONE)
      }
    },
    [actions.createMessage] ({ commit, state }, message) {
      if (message.text) {
        commit('CREATE_MESSAGE', message)
      }

      return state.messages
    }
  },

  modules: { },
  plugins: [
    vuexAiPlugin,
    persistentUsersPlugin
  ]
}
)
